import { useCallback } from "react";
import { Box, Typography } from "@mui/material";
import {
  UpdateEventFeedbackInput,
  UpdateEventResponse,
} from "../../../common/types/events/api";
import { EventViewModel } from "../models/eventViewModel";
import confirmDialogStyleOptions from "../../../common/helpers/confirmDialogParams";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useConfirm } from "material-ui-confirm";
import { ApolloError } from "@apollo/client";

export function useFeedbackUpdateConfirmDialog(
  updateEventFeedback: (
    input: UpdateEventFeedbackInput
  ) => Promise<UpdateEventResponse | undefined>,
  error: ApolloError | undefined
) {
  const confirm = useConfirm();

  return useCallback(
    async (input: UpdateEventFeedbackInput, event: EventViewModel) => {
      const isFeedbackChanged = input.feedback !== event.feedback;

      // If only updating feedback details, skip confirmation modal
      if (!isFeedbackChanged) {
        const result = await updateEventFeedback(input);

        if (error || !result) {
          errorNotification("Something went wrong");
        } else {
          successNotification("Event feedback updated successfully");
        }

        return;
      }

      let title: React.ReactNode = (
        <Typography variant="h5" align="center">
          Are you sure you want to update the event feedback?
        </Typography>
      );

      if (input.feedback === "POSITIVE") {
        const hasFeedbackDetails =
          event.mitigatedRisk && event.mitigatedRisk.length > 0;

        if (hasFeedbackDetails) {
          title = (
            <Box>
              <Typography variant="h3" align="center">
                Are you sure you want to change this to positive?
              </Typography>
              <Typography
                variant="body1"
                align="center"
                color="text.secondary"
                sx={{ mt: 2, mr: 3, ml: 3 }}
              >
                Switching to positive feedback will remove all selected feedback
                details.
              </Typography>
            </Box>
          );
        } else {
          title = (
            <Typography variant="h3" align="center">
              Are you sure you want to change this to positive?
            </Typography>
          );
        }
      } else if (input.feedback === "NEGATIVE") {
        const hasFeedbackDetails =
          event.mitigatedRisk && event.mitigatedRisk.length > 0;

        if (hasFeedbackDetails) {
          title = (
            <Box>
              <Typography variant="h3" align="center">
                Are you sure you want to change this to negative?
              </Typography>
              <Typography
                variant="body1"
                align="center"
                color="text.secondary"
                sx={{ mt: 2, mr: 3, ml: 3 }}
              >
                Switching to negative feedback will remove all selected feedback
                details.
              </Typography>
            </Box>
          );
        } else {
          title = (
            <Typography variant="h3" align="center">
              Are you sure you want to change this to negative?
            </Typography>
          );
        }
      }

      try {
        await confirm({
          title,
          confirmationText: "Confirm",
          cancellationText: "Cancel",
          ...confirmDialogStyleOptions,
          confirmationButtonProps: {
            ...confirmDialogStyleOptions.confirmationButtonProps,
            color: "primary",
          },
        });

        const result = await updateEventFeedback(input);

        if (error || !result) {
          errorNotification("Something went wrong");
        } else {
          successNotification("Event feedback updated successfully");
        }
      } catch (confirmError) {
        // User cancelled the confirmation dialog - this is expected behavior
      }
    },
    [updateEventFeedback, error, confirm]
  );
}
